// ============================================================
// TERROR TCG — CHAIN LAW
// ============================================================
// ONE law, THREE surfaces. A true counter is itself an activation,
// so it can be answered — and that answer can be answered, and so on.
// This module is the single place that knows:
//
//   • WHICH cards may answer WHAT (the legality law), and
//   • HOW a finished chain resolves (the LIFO law).
//
// It is pure: no React, no Firebase, no imports. Shatterfield's
// responder window, its rebuttal window, and NPC.js's reactive brain
// all read the same functions, so the three surfaces cannot drift —
// which is exactly how the July law drifted into three near-copies
// that agreed by luck.
//
// ── THE LEGALITY LAW (canon, July 2026) ──────────────────────
//   • A L3 Nihil/Omnia Tome answers from HAND — the true instants.
//   • A face-down L2/L3 Nihil/Omnia answers from the FIELD — the
//     ambushes.
//
//   ⚠ AND THE BREADTH IS DELIBERATE — DO NOT "FIX" IT. The right to
//   answer is drawn by SCHOOL (Nihil / Omnia) and by LEVEL, not by a
//   curated list of which card may usefully reply to which. So any L2
//   Nihil or Omnia may answer on the adversary's Turn even when the
//   answer accomplishes nothing at all. caP'n, 2026-08-26, asked
//   directly whether that was right:
//
//     *"technically any level 2 can counter on your opponent's turn
//     already, right? But does it do anything useful? Doesn't have to.
//     This way it's a broad blanket for the Tomes of 'hmm what is it'
//     as it sits there face down."*
//
//   THE BLUFF IS THE MECHANIC. A face-down Tome that COULD be an
//   answer is worth something whether or not it is one; narrowing this
//   to the answers that "do something" would empty the rift of every
//   ounce of that. Written down here because a future reader will find
//   a useless-but-legal counter and take it for an oversight. It is not.
//
//   • COUNTER HIERARCHY: only a L3+ activation is shielded from
//     lesser answers. A L1/L2 answer to a L3+ activation is LATE —
//     it cannot pre-empt; the effect resolves FIRST, then the
//     activated card must leave the field.
//   • CHAIN LAW (rebuttals): an answer to an answer must be
//     same-or-higher level, and is NEVER late. You cannot rebut a
//     L3 counter with a L2 ambush.
//   • A LINK THAT WAS ANSWERED STILL PAID. Nihil voids the EFFECT, not
//     the fare: the Khao is gone and the firing fizzles. That is what
//     this bullet has always been about, and it is still true.
//
//   ⚠ AND THE SENTENCE THIS BULLET ONCE CARRIED IS BACK, BY RULING.
//   It read "Fares are always paid, at every link, forever". On
//   2026-08-26 that was struck as a licence to re-charge a persistent
//   card, and R105 was written as "paid once and that's it". Asked the
//   question directly on 2026-09-13, caP'n ruled the other way:
//   *"you must pay its fare again, thats the cost of it being able to
//   stay on the field 'indefinitely'."* So the original line was right
//   and it stands: EVERY activation pays the printed fare, at every
//   link, forever — and a re-activation that gets answered has lost a
//   real fare, which is what makes answering one worth doing. See
//   rules.js · activationFare. Both readings are recorded here on
//   purpose: this bullet has now been written three times and a future
//   reader deserves to see that it was argued, not drifted.
//
// ── THE LIFO LAW (C12, August 2026) ──────────────────────────
// The stack resolves from the TOP down. The last answer played has
// nothing above it, so it always stands; the link beneath it is
// voided; the link beneath THAT stands again (its voider was
// voided) — effectiveness alternates down the stack.
//
//   chain[0] = the counter that answered the ORIGINAL declaration.
//   → the declared action is countered exactly when the chain has an
//     ODD number of links.
//
// A ruined card follows the same alternation: an Omnia only ruins the
// link below it if that Omnia is itself effective. A Nihil that gets
// nullified never voided anything.
// ============================================================

// ── THE CHAIN HAS NO LENGTH LIMIT (caP'n, 2026-08-26) ────────
// Runaway guard, NOT a rule. The real bound on a chain is cards and
// Khao — both sides run out of L3 instants long before this. It exists
// so a bug can never spin an infinite window loop, and it is a DIAL:
// raise it freely, nothing else in the engine reads a chain's length.
//
// THE DIAL WAS TURNED, and the designer's own words are why. caP'n,
// verbatim:
//
//   *"as long as they want that's the point — you said it, players will
//   run out of cards but mainly Khao in like 2 or 3 chains max, it will
//   never get to 12 and if it does that'd be cool xD but like impossible
//   to even do 5 unless both players sat there trying to make it happen
//   over and over using super low cost cards"*
//
// 12 was documented as a non-rule and was STILL low enough to truncate a
// legal chain the designer says he would enjoy seeing. 64 cannot: it
// would need thirty-two L3 instants a side inside one window, which no
// legal deck can hold. A chain that reaches it is a LOOP, not a duel.
// This is a guard against a bug, and nothing else. See the fuller banner
// in rules.js, which carries the same constant and the same quote.
export const CHAIN_DEPTH_CAP = 64;

/** A true counter: a Tome whose ilk answers cards rather than beings. */
export const isTrueCounter = (c) =>
  !!c && c.type === "Tome" && (c.ilk === "Nihil" || c.ilk === "Omnia");

/**
 * Every legal, affordable TRUE COUNTER a seat holds RIGHT NOW.
 * Callers pass their own fresh state — this function never reads any.
 *
 * @param {object}   p
 * @param {string[]} p.hand           card ids in hand
 * @param {Array}    p.tomes          the seat's 3 tome rifts (objects or null)
 * @param {number}   p.khao           Khao available right now
 * @param {function} p.getCard        id → card record
 * @param {number}   p.declaredLevel  level of the card being answered
 * @param {boolean}  p.isRebuttal     true when answering an ANSWER (chain law)
 * @returns {Array}  [{ source, idx, cardId, ilk, level, late }]
 */
export function trueCounterOptions({
  hand = [], tomes = [], khao = 0, getCard,
  declaredLevel = 1, isRebuttal = false,
}) {
  const out = [];
  (hand || []).forEach((id, i) => {
    const c = getCard(id);
    if (!isTrueCounter(c) || (c.level || 1) !== 3) return; // hand instants are L3 by law
    if ((c.khaoCost || 0) > khao) return;
    out.push({ source: "hand", idx: i, cardId: id, ilk: c.ilk, level: 3, late: false });
  });
  (tomes || []).forEach((t, i) => {
    if (!t || typeof t !== "object" || !t.faceDown) return;
    const c = getCard(t.id);
    if (!isTrueCounter(c) || (c.level || 1) < 2) return;   // ambushes are L2+
    if ((c.khaoCost || 0) > khao) return;
    out.push({
      source: "field", idx: i, cardId: t.id, ilk: c.ilk, level: c.level || 1,
      late: declaredLevel >= 3 && (c.level || 1) < 3,
    });
  });
  // CHAIN LAW — same-or-higher level, never late.
  return isRebuttal
    ? out.filter(o => !o.late && o.level >= declaredLevel)
    : out;
}

/**
 * Does link `idx` (0-based) actually take effect, in a chain of `len`?
 * The top link always does; effectiveness alternates downward.
 */
export const isLinkEffective = (idx, len) => ((len - 1 - idx) % 2) === 0;

/**
 * Did the ORIGINAL declared action get countered? True on an odd chain
 * (chain[0] survived every rebuttal above it).
 */
export const chainStands = (len) => len > 0 && (len % 2) === 1;

/**
 * Which links get RUINED off the field: a link answered by an
 * EFFECTIVE Omnia above it. (Nihil voids the effect and leaves the
 * card wherever its own lifecycle put it; one-shots already rode to a
 * Necropolis, so only a persistent survivor has anywhere to fall from.)
 * @returns {Array} the ruined links, each still carrying its own fields
 */
export const ruinedLinks = (chain = []) =>
  chain.filter((_, i) => {
    const above = chain[i + 1];
    return !!above && above.ilk === "omnia" && isLinkEffective(i + 1, chain.length);
  });

/** Human-readable chain, for toasts and the Record. */
export const describeChain = (chain = []) =>
  chain.map(l => l.name || "an answer").join(" → ");

export default {
  CHAIN_DEPTH_CAP, isTrueCounter, trueCounterOptions,
  isLinkEffective, chainStands, ruinedLinks, describeChain,
};
